/** Timing, framing, and mime choice for the record studio. No media devices. */

export const RECORD_LIMIT_MS = 30 * 60 * 1000;

export const RECORD_BACKDROPS = [
  { id: 'none', label: 'None', color: '#000000' },
  { id: 'dusk', label: 'Dusk', color: '#2a2140' },
  { id: 'ink', label: 'Ink', color: '#16181d' },
  { id: 'sand', label: 'Sand', color: '#c4a574' },
] as const;

export const RECORD_EFFECTS = [
  { id: 'none', label: 'None', filter: 'none' },
  { id: 'warm', label: 'Warm', filter: 'sepia(0.35) saturate(1.15)' },
  { id: 'cool', label: 'Cool', filter: 'saturate(0.8) hue-rotate(16deg)' },
  { id: 'mono', label: 'Mono', filter: 'grayscale(1)' },
] as const;

export type RecordBackdropId = (typeof RECORD_BACKDROPS)[number]['id'];
export type RecordEffectId = (typeof RECORD_EFFECTS)[number]['id'];

export function recordClocks(elapsedMs: number, limitMs = RECORD_LIMIT_MS): { elapsed: string; remaining: string } {
  const elapsed = Math.max(0, Math.min(limitMs, Math.floor(elapsedMs)));
  const remaining = Math.max(0, limitMs - elapsed);
  return { elapsed: formatClock(elapsed), remaining: formatClock(remaining) };
}

function formatClock(ms: number): string {
  const total = Math.floor(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Fit a source inside a max box, keeping even dimensions for the encoder. */
export function fitSize(srcW: number, srcH: number, max = 1280): { w: number; h: number } {
  const width = Math.max(2, srcW);
  const height = Math.max(2, srcH);
  const scale = Math.min(1, max / Math.max(width, height));
  const even = (value: number): number => Math.max(2, Math.round((value * scale) / 2) * 2);
  return { w: even(width), h: even(height) };
}

/** Source rectangle that covers a destination, center-cropped. */
export function coverRect(
  srcW: number,
  srcH: number,
  dstW: number,
  dstH: number
): { sx: number; sy: number; sw: number; sh: number } {
  const scale = Math.max(dstW / srcW, dstH / srcH);
  const sw = dstW / scale;
  const sh = dstH / scale;
  return { sx: (srcW - sw) / 2, sy: (srcH - sh) / 2, sw, sh };
}

/** Camera picture-in-picture, bottom-left, when a screen share fills the frame. */
export function pipBox(frameW: number, frameH: number): { x: number; y: number; w: number; h: number } {
  const w = Math.round(frameW * 0.28);
  const h = Math.round(w * 0.75);
  return { x: 16, y: Math.max(16, frameH - h - 16), w, h };
}

/** Camera inset used when a color backdrop should stay visible. */
export function insetBox(frameW: number, frameH: number): { x: number; y: number; w: number; h: number } {
  const w = Math.round(frameW * 0.78);
  const h = Math.round(frameH * 0.78);
  return { x: Math.round((frameW - w) / 2), y: Math.round((frameH - h) / 2), w, h };
}

/**
 * MediaRecorder WebM often reports Infinity until the browser is forced to
 * seek past the end. A clock hint still places the clip when metadata never settles.
 */
export function usableDurationMs(reportedSeconds: number, hintMs = 0): number {
  if (Number.isFinite(reportedSeconds) && reportedSeconds > 0 && reportedSeconds < 1e7) {
    return Math.round(reportedSeconds * 1000);
  }
  if (Number.isFinite(hintMs) && hintMs > 0) return Math.max(100, Math.round(hintMs));
  return 0;
}

export function pickRecorderMime(kind: 'video' | 'audio'): string {
  const candidates =
    kind === 'audio'
      ? ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
      : ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
  const canCheck = typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function';
  const supported = canCheck ? candidates.find((item) => MediaRecorder.isTypeSupported(item)) : undefined;
  return supported ?? (kind === 'audio' ? 'audio/webm' : 'video/webm');
}

/**
 * MediaRecorder writes the WebM duration as an endless float, so a player
 * seeks back to the first frame and the take looks like a photo.
 * The duration field is milliseconds when the file's timecode scale is 1 ms.
 */
export async function patchWebmDuration(blob: Blob, durationMs: number): Promise<Blob> {
  if (!blob.type.toLowerCase().includes('webm') || !(durationMs > 0)) return blob;
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
    const limit = Math.min(bytes.length - 11, 2048);
    for (let i = 0; i < limit; i += 1) {
      if (bytes[i] !== 0x44 || bytes[i + 1] !== 0x89) continue;
      const size = bytes[i + 2];
      if (size === 0x88) {
        const current = view.getFloat64(i + 3);
        if (Number.isFinite(current) && current > 0) continue;
        view.setFloat64(i + 3, durationMs);
        return new Blob([buffer], { type: blob.type });
      }
      if (size === 0x84) {
        const current = view.getFloat32(i + 3);
        if (Number.isFinite(current) && current > 0) continue;
        view.setFloat32(i + 3, durationMs);
        return new Blob([buffer], { type: blob.type });
      }
    }
  return blob;
}
